import { useQuery } from '@tanstack/react-query';
import {
  Award,
  BookOpen,
  CheckCircle,
  Clock,
  Download,
  HelpCircle,
  Lock,
  LucideIcon,
  MapPin,
  MessageCircle,
  Search,
  Ticket,
  ToggleLeft,
  ToggleRight,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { TICKET_REQUESTS_PERMISSION } from '@/constants/constant-permissions';

import Modal from '@/components/ui/Modal';
import Pagination from '@/components/ui/Pagination';
import SimpleSelect, { SelectOption } from '@/components/ui/SimpleSelect';
import { SkeletonLoader, TableSkeleton } from '@/components/ui/SkeletonLoader';
import { ticketService, ApproverDropdownItem } from '@/services/ticket.service';
import {
  SelfTicketItemType,
  SelfTicketRequestResponseDto,
  SelfTicketRequestStatus,
  SelfTicketRequestStatsDto,
} from '@/types/ticket.types';
import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import { exportService } from '@/services/export.service';
import toast from 'react-hot-toast';

// Type config interface
interface TypeConfig {
  icon: LucideIcon;
  label: string;
  iconBg: string;
  iconColor: string;
}

// Status config interface
interface StatusConfig {
  bg: string;
  text: string;
  icon: LucideIcon;
  label: string;
}

// Type configurations map
const typeConfigMap: Record<SelfTicketItemType, TypeConfig> = {
  [SelfTicketItemType.COURSE]: {
    icon: BookOpen,
    label: 'Course',
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
  },
  [SelfTicketItemType.CERTIFICATION]: {
    icon: Award,
    label: 'Certification',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  [SelfTicketItemType.LEARNING_PATH]: {
    icon: MapPin,
    label: 'Learning Path',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
  },
  [SelfTicketItemType.GENERAL]: {
    icon: HelpCircle,
    label: 'General',
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
  },
};

// Status configurations map
const statusConfigMap: Record<SelfTicketRequestStatus, StatusConfig> = {
  [SelfTicketRequestStatus.PENDING]: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    icon: Clock,
    label: 'Pending',
  },
  [SelfTicketRequestStatus.APPROVED]: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    icon: CheckCircle,
    label: 'Approved',
  },
  [SelfTicketRequestStatus.DENIED]: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    icon: XCircle,
    label: 'Denied',
  },
  [SelfTicketRequestStatus.CLOSED]: {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    icon: Lock,
    label: 'Closed',
  },
};

// Filter options
const statusFilterOptions: SelectOption[] = [
  { label: 'All Status', value: '' },
  { label: 'Pending', value: SelfTicketRequestStatus.PENDING },
  { label: 'Approved', value: SelfTicketRequestStatus.APPROVED },
  { label: 'Denied', value: SelfTicketRequestStatus.DENIED },
  { label: 'Closed', value: SelfTicketRequestStatus.CLOSED },
];

const typeFilterOptions: SelectOption[] = [
  { label: 'All Types', value: '' },
  { label: 'Course', value: SelfTicketItemType.COURSE },
  { label: 'Certification', value: SelfTicketItemType.CERTIFICATION },
  { label: 'Learning Path', value: SelfTicketItemType.LEARNING_PATH },
  { label: 'General', value: SelfTicketItemType.GENERAL },
];

// Format date to dd/mm/yyyy
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Status badge component
const StatusBadge = ({ status }: { status: SelfTicketRequestStatus }): JSX.Element => {
  const config = statusConfigMap[status];
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </span>
  );
};

// Item type badge component
const ItemTypeBadge = ({ type }: { type: SelfTicketItemType }): JSX.Element => {
  const config = typeConfigMap[type];
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.iconBg} ${config.iconColor}`}>
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </span>
  );
};

// Page skeleton for initial loading
const PageSkeleton = (): JSX.Element => (
  <div className="space-y-6">
    {/* Header with Stats skeleton */}
    <div className="bg-gradient-to-r from-brand-violet-600 to-brand-purple-700 rounded-xl p-6">
      <div className="space-y-3 mb-6">
        <div className="flex items-center gap-3">
          <SkeletonLoader type="custom" className="w-8 h-8 rounded bg-white/20" />
          <SkeletonLoader type="custom" className="h-8 w-48 rounded bg-white/20" />
        </div>
        <SkeletonLoader type="custom" className="h-4 w-96 rounded bg-white/20" />
      </div>
      {/* Stats skeleton inside header */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
            <SkeletonLoader type="custom" className="h-16 rounded bg-white/20" />
          </div>
        ))}
      </div>
    </div>

    {/* Table card skeleton */}
    <div className="bg-white rounded-xl shadow-sm border">
      <div className="p-4 border-b bg-gray-50">
        <div className="flex flex-col sm:flex-row gap-4">
          <SkeletonLoader type="custom" className="h-10 flex-1 rounded-lg" />
          <SkeletonLoader type="custom" className="h-10 w-[140px] rounded-lg" />
          <SkeletonLoader type="custom" className="h-10 w-[140px] rounded-lg" />
        </div>
      </div>
      <TableSkeleton rows={5} columns={7} showHeader={true} />
    </div>
  </div>
);

// Get initials from name (first + last)
const getInitials = (name: string): string => {
  const parts = name.split(' ').filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Items Detail Modal Props
interface ItemsDetailModalProps {
  ticket: SelfTicketRequestResponseDto | null;
  isOpen: boolean;
  onClose: () => void;
}

// Items Detail Modal Content
const ItemsDetailModal = ({ ticket, isOpen, onClose }: ItemsDetailModalProps): JSX.Element | null => {
  if (!ticket) return null;

  const typeConfig = typeConfigMap[ticket.item_type];
  const statusConfig = statusConfigMap[ticket.status];
  const TypeIcon = typeConfig.icon;
  const StatusIcon = statusConfig.icon;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Ticket Details"
      size="md"
    >
      <div className="space-y-5">
        {/* Status & Type Row */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${typeConfig.iconBg}`}>
              <TypeIcon className={`w-5 h-5 ${typeConfig.iconColor}`} />
            </div>
            <span className="text-sm font-medium text-gray-700">{typeConfig.label} Request</span>
          </div>
          <div className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${statusConfig.bg} ${statusConfig.text}`}>
            <StatusIcon className="w-3.5 h-3.5" />
            {statusConfig.label}
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Requested By</p>
            <p className="font-medium text-gray-900">{ticket.requester_name}</p>
            <p className="text-sm text-gray-500 truncate">{ticket.requester_email}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Requested Date</p>
            <p className="font-medium text-gray-900">{formatDate(ticket.createdAt)}</p>
            <p className="text-sm text-gray-500">{ticket.items_count} item{ticket.items_count > 1 ? 's' : ''}</p>
          </div>
        </div>

        {/* For GENERAL tickets - show description, for others - show items */}
        {ticket.item_type === SelfTicketItemType.GENERAL ? (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Request Description</p>
            <div className="p-3 bg-brand-purple-50 rounded-lg border border-brand-purple-200">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {ticket.general_description || 'No description provided'}
              </p>
            </div>
            {/* Show Suggested Items if ticket has been actioned */}
            {ticket.actioned_by?.action === 'SUGGESTED' && ticket.items && ticket.items.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Suggested Items</p>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {ticket.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-brand-purple-50 rounded-lg border border-brand-purple-200"
                    >
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-purple-100 flex items-center justify-center">
                        <span className="text-xs font-semibold text-brand-purple-600">{index + 1}</span>
                      </div>
                      <p className="text-sm font-medium text-gray-900 truncate flex-1 min-w-0">{item.item_name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Requested Items</p>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {ticket.items.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-xs font-semibold text-gray-600">{index + 1}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900 truncate flex-1 min-w-0">{item.item_name}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Request Message - only for non-GENERAL tickets */}
        {ticket.item_type !== SelfTicketItemType.GENERAL && ticket.request_message && (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Message</p>
            <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 border border-gray-200">
              {ticket.request_message}
            </p>
          </div>
        )}

        {/* Resolution */}
        {(ticket.approved_by || ticket.denied_by) && (
          <div className="pt-4 border-t border-gray-200">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Resolution</p>
            {ticket.approved_by && (
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Approved by {ticket.approved_by.user_name}</p>
                  <p className="text-xs text-gray-500">{ticket.approved_by.role}</p>
                </div>
              </div>
            )}
            {ticket.denied_by && (
              <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Denied by {ticket.denied_by.user_name}</p>
                  <p className="text-xs text-gray-500">{ticket.denied_by.role}</p>
                  {ticket.denied_by.reason && (
                    <p className="text-sm text-red-700 mt-2">Reason: {ticket.denied_by.reason}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

// Storage key for persisting filter state
const TICKET_FILTERS_KEY = 'admin_ticket_filters';

// Main Component
export default function AdminTicketRequests(): JSX.Element {
  usePermissionRouteAccess([
    TICKET_REQUESTS_PERMISSION.ADMIN_TICKET_REQUESTS_READ,
    TICKET_REQUESTS_PERMISSION.ADMIN_TICKET_REQUESTS_WRITE,
  ]);
  const canWrite = useHasPermission([TICKET_REQUESTS_PERMISSION.ADMIN_TICKET_REQUESTS_WRITE]);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, DEFAULT_DEBOUNCE_DELAY);
  const [statusFilter, setStatusFilter] = useState<SelfTicketRequestStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<SelfTicketItemType | ''>('');
  const [approverFilter, setApproverFilter] = useState<string>('');
  // Read myRequestsOnly from sessionStorage, default to true if not set
  const [myRequestsOnly, setMyRequestsOnly] = useState(() => {
    try {
      const stored = sessionStorage.getItem(TICKET_FILTERS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.myRequestsOnly ?? true;
      }
    } catch {
      // Ignore parse errors
    }
    return true;
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedTicket, setSelectedTicket] = useState<SelfTicketRequestResponseDto | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (format: 'csv' | 'excel') => {
    setIsExporting(true);
    try {
      const result = await exportService.exportApproverRequests(format, {
        search: debouncedSearch || undefined,
        status: statusFilter || undefined,
        item_type: typeFilter || undefined,
        approver_user_id: approverFilter || undefined,
        my_requests_only: approverFilter ? false : myRequestsOnly,
      });
      if (result.success) {
        toast.success(`Exported ${result.count} ticket requests`);
      } else {
        toast.error(result.message || 'No data to export');
      }
    } catch (error) {
      toast.error('Failed to export ticket requests');
    } finally {
      setIsExporting(false);
    }
  };

  // Persist myRequestsOnly to sessionStorage when it changes
  useEffect(() => {
    try {
      sessionStorage.setItem(TICKET_FILTERS_KEY, JSON.stringify({ myRequestsOnly }));
    } catch {
      // Ignore storage errors
    }
  }, [myRequestsOnly]);

  // Fetch approvers dropdown
  const { data: approversData } = useQuery({
    queryKey: ['approvers-dropdown'],
    queryFn: () => ticketService.getApproversDropdown(),
  });

  // Create approver filter options from API data
  const approverFilterOptions: SelectOption[] = [
    { label: 'All Approvers', value: '' },
    ...(approversData?.data?.map((approver: ApproverDropdownItem) => ({
      label: `${approver.name} (${approver.email})`,
      value: approver._id,
    })) || []),
  ];

  // Fetch tickets using React Query
  const {
    data: ticketsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [
      'received-ticket-requests',
      currentPage,
      itemsPerPage,
      statusFilter,
      typeFilter,
      myRequestsOnly,
      approverFilter,
      debouncedSearch,
    ],
    queryFn: () =>
      ticketService.getReceivedRequests({
        page: currentPage,
        limit: itemsPerPage,
        status: statusFilter || undefined,
        item_type: typeFilter || undefined,
        my_requests_only: approverFilter ? false : myRequestsOnly, // Disable my_requests_only when approver filter is active
        approver_user_id: approverFilter || undefined,
        search: debouncedSearch || undefined,
      }),
  });

  // Fetch request stats
  const { data: statsData } = useQuery<SelfTicketRequestStatsDto>({
    queryKey: ['ticket-request-stats'],
    queryFn: () => ticketService.getRequestStats(),
  });

  // Get tickets data
  const tickets = ticketsData?.data ?? [];
  const pagination = ticketsData?.pagination;

  // Handle page change
  const handlePageChange = (page: number): void => {
    setCurrentPage(page);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (perPage: number): void => {
    setItemsPerPage(perPage);
    setCurrentPage(1);
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, typeFilter, myRequestsOnly, approverFilter, debouncedSearch]);

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <XCircle className="w-16 h-16 mb-4 text-red-400" />
        <p className="font-semibold text-lg">Failed to load ticket requests</p>
        <p className="text-sm mt-1">Please try again later</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header with Stats */}
      <div className="bg-gradient-to-r from-brand-violet-600 to-brand-purple-700 rounded-xl p-4 sm:p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 sm:gap-3">
            <Ticket className="w-6 h-6 sm:w-8 sm:h-8" />
            <h1 className="text-xl sm:text-2xl font-bold">Ticket Requests</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => canWrite && handleExport('csv')}
              disabled={isExporting || tickets.length === 0 || !canWrite}
              title={!canWrite ? 'You do not have permission' : undefined}
              className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg bg-white/15 hover:bg-white/25 border border-white/20 text-white text-xs sm:text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              CSV
            </button>
            <button
              onClick={() => canWrite && handleExport('excel')}
              disabled={isExporting || tickets.length === 0 || !canWrite}
              title={!canWrite ? 'You do not have permission' : undefined}
              className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg bg-white/15 hover:bg-white/25 border border-white/20 text-white text-xs sm:text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              Excel
            </button>
          </div>
        </div>
        <p className="text-brand-violet-200 text-sm sm:text-base mb-4 sm:mb-6">
          Manage and review self-assignment requests from learners
        </p>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-brand-violet-200 font-medium">Total Requests</p>
                <p className="text-lg sm:text-2xl font-bold text-white mt-1">{statsData?.total_count ?? 0}</p>
              </div>
              <div className="p-2 sm:p-3 rounded-xl bg-white/20 hidden sm:block">
                <Ticket className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-brand-violet-200 font-medium">Pending</p>
                <p className="text-lg sm:text-2xl font-bold text-white mt-1">{statsData?.pending_count ?? 0}</p>
              </div>
              <div className="p-2 sm:p-3 rounded-xl bg-white/20 hidden sm:block">
                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-brand-violet-200 font-medium">Denied</p>
                <p className="text-lg sm:text-2xl font-bold text-white mt-1">{statsData?.denied_count ?? 0}</p>
              </div>
              <div className="p-2 sm:p-3 rounded-xl bg-white/20 hidden sm:block">
                <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-brand-violet-200 font-medium">Approved</p>
                <p className="text-lg sm:text-2xl font-bold text-white mt-1">{statsData?.approved_count ?? 0}</p>
              </div>
              <div className="p-2 sm:p-3 rounded-xl bg-white/20 hidden sm:block">
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-xl shadow-sm border">
        {/* Filters */}
        <div className="p-3 sm:p-4 border-b bg-gray-50">
          <div className="flex flex-col lg:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by ticket no, requester, or request..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
            </div>

            <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-3">
              {/* Status Filter */}
              <div className="sm:w-[150px]">
                <SimpleSelect
                  value={statusFilter}
                  onChange={(value) => setStatusFilter((value as SelfTicketRequestStatus) || '')}
                  options={statusFilterOptions}
                  placeholder="All Status"
                  isSearchable={false}
                  isClearable={false}
                />
              </div>

              {/* Type Filter */}
              <div className="sm:w-[160px]">
                <SimpleSelect
                  value={typeFilter}
                  onChange={(value) => setTypeFilter((value as SelfTicketItemType) || '')}
                  options={typeFilterOptions}
                  placeholder="All Types"
                  isSearchable={false}
                  isClearable={false}
                />
              </div>

              {/* Approver Filter */}
              <div className="col-span-2 sm:col-span-1 sm:flex-1 sm:min-w-[200px]">
                <SimpleSelect
                  value={approverFilter}
                  onChange={(value) => {
                    setApproverFilter((value as string) || '');
                    if (value) {
                      setMyRequestsOnly(false);
                    }
                  }}
                  options={approverFilterOptions}
                  placeholder="All Approvers"
                  isSearchable={true}
                  isClearable={false}
                />
              </div>

              {/* My Requests Toggle */}
              <button
                onClick={() => {
                  setMyRequestsOnly(!myRequestsOnly);
                  if (!myRequestsOnly) {
                    setApproverFilter('');
                  }
                }}
                disabled={!!approverFilter}
                className={`col-span-2 sm:col-span-1 flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 justify-center sm:justify-start ${
                  approverFilter
                    ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                    : myRequestsOnly
                      ? 'bg-primary-50 border-primary-300 text-primary-700'
                      : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {myRequestsOnly && !approverFilter ? (
                  <ToggleRight className="w-5 h-5 text-primary-600" />
                ) : (
                  <ToggleLeft className="w-5 h-5 text-gray-400" />
                )}
                <span className="text-sm font-medium whitespace-nowrap">My Requests</span>
              </button>
            </div>
          </div>
        </div>

        {/* Table (desktop) / Cards (mobile) */}
        {tickets.length > 0 ? (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Ticket No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Requester
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Requested For
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Requested Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Ticket Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tickets.map((ticket: SelfTicketRequestResponseDto) => (
                    <tr
                      key={ticket._id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-4">
                        <span className="font-mono text-sm font-semibold text-primary-600">
                          {ticket.ticket_number}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-brand-indigo-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {ticket.requester_profile_image ? (
                              <img
                                src={ticket.requester_profile_image}
                                alt={ticket.requester_name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-white font-semibold text-sm">
                                {getInitials(ticket.requester_name)}
                              </span>
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {ticket.requester_name}
                            </p>
                            <p className="text-xs text-gray-500">{ticket.requester_email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <ItemTypeBadge type={ticket.item_type} />
                      </td>
                      <td className="px-4 py-4">
                        {ticket.item_type === SelfTicketItemType.GENERAL ? (
                          <div>
                            <p className="text-sm text-gray-900 line-clamp-1">
                              {ticket.general_description || 'No description'}
                            </p>
                            {ticket.general_description && ticket.general_description.length > 50 && (
                              <button
                                onClick={() => setSelectedTicket(ticket)}
                                className="text-xs text-brand-purple-600 font-medium mt-0.5 hover:text-brand-purple-800 hover:underline"
                              >
                                Show more
                              </button>
                            )}
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm text-gray-900">
                              {ticket.items[0]?.item_name ?? 'No items'}
                            </p>
                            {ticket.items_count > 1 && (
                              <button
                                onClick={() => setSelectedTicket(ticket)}
                                className="text-xs text-primary-600 font-medium mt-0.5 hover:text-primary-800 hover:underline"
                              >
                                +{ticket.items_count - 1} more item{ticket.items_count > 2 ? 's' : ''}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-xs text-gray-500">{formatDate(ticket.createdAt)}</p>
                      </td>
                      <td className="px-4 py-4">
                        <StatusBadge status={ticket.status} />
                      </td>
                      <td className="px-4 py-4">
                        <button
                          onClick={() => canWrite && navigate(`/admin/settings/ticket-requests/${ticket._id}`)}
                          disabled={!canWrite}
                          title={!canWrite ? 'You do not have permission' : undefined}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors text-primary-600 bg-primary-50 hover:bg-primary-100${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <MessageCircle className="w-4 h-4" />
                          Chat
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-200">
              {tickets.map((ticket: SelfTicketRequestResponseDto) => (
                <div key={ticket._id} className="p-4 space-y-3">
                  {/* Top row: ticket number + status */}
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm font-semibold text-primary-600">
                      {ticket.ticket_number}
                    </span>
                    <StatusBadge status={ticket.status} />
                  </div>

                  {/* Requester */}
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-brand-indigo-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {ticket.requester_profile_image ? (
                        <img
                          src={ticket.requester_profile_image}
                          alt={ticket.requester_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-white font-semibold text-xs">
                          {getInitials(ticket.requester_name)}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {ticket.requester_name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{ticket.requester_email}</p>
                    </div>
                  </div>

                  {/* Type + Requested For */}
                  <div className="flex items-start gap-2">
                    <ItemTypeBadge type={ticket.item_type} />
                    <p className="text-sm text-gray-700 line-clamp-2 flex-1">
                      {ticket.item_type === SelfTicketItemType.GENERAL
                        ? (ticket.general_description || 'No description')
                        : (ticket.items[0]?.item_name ?? 'No items')}
                      {ticket.item_type !== SelfTicketItemType.GENERAL && ticket.items_count > 1 && (
                        <button
                          onClick={() => setSelectedTicket(ticket)}
                          className="text-xs text-primary-600 font-medium ml-1 hover:underline"
                        >
                          +{ticket.items_count - 1} more
                        </button>
                      )}
                    </p>
                  </div>

                  {/* Date + Action */}
                  <div className="flex items-center justify-between pt-1">
                    <p className="text-xs text-gray-500">{formatDate(ticket.createdAt)}</p>
                    <button
                      onClick={() => canWrite && navigate(`/admin/settings/ticket-requests/${ticket._id}`)}
                      disabled={!canWrite}
                      title={!canWrite ? 'You do not have permission' : undefined}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors text-primary-600 bg-primary-50 hover:bg-primary-100${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <MessageCircle className="w-4 h-4" />
                      Chat
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 sm:py-16 text-gray-500 px-4">
            <Ticket className="w-12 h-12 sm:w-16 sm:h-16 mb-3 sm:mb-4 opacity-30" />
            <p className="font-semibold text-base sm:text-lg text-center">No ticket requests found</p>
            <p className="text-xs sm:text-sm mt-1 text-center">
              {statusFilter || typeFilter || debouncedSearch
                ? 'Try adjusting your filters'
                : myRequestsOnly
                  ? 'Ticket requests assigned to you will appear here'
                  : 'Ticket requests from learners will appear here'}
            </p>
          </div>
        )}

        {/* Pagination */}
        {tickets.length > 0 && pagination && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.total_pages}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            showItemsPerPage={true}
            itemsPerPageOptions={[10, 20, 50]}
          />
        )}
      </div>

      {/* Items Detail Modal */}
      <ItemsDetailModal
        ticket={selectedTicket}
        isOpen={!!selectedTicket}
        onClose={() => setSelectedTicket(null)}
      />
    </div>
  );
}
